<template>
    <cic-popup
        :title="item.siteId !== null ? 'Edit Site' : 'New Site'"
        :saveable="editing"
        @blur="$emit('blur')"
        @input="$emit('input')"
    >
        <div class="item-form">
            <ul>
                <template v-if="item.siteId !== 0">
                    <li class="form-label-first">
                        {{ $t('Site Name') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="site_name"
                            :enabled="editing"
                            :value="item.name"
                            :maxlength="256"
                            @input="(val) => updateFieldValue('name', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('Address Line 1') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="site_address1"
                            :enabled="editing"
                            :value="item.address1"
                            :maxlength="64"
                            @input="(val) => updateFieldValue('address1', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('Address Line 2') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="site_address2"
                            :enabled="editing"
                            :value="item.address2"
                            :maxlength="64"
                            @input="(val) => updateFieldValue('address2', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('City') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="site_city"
                            :enabled="editing"
                            :value="item.city"
                            :maxlength="64"
                            @input="(val) => updateFieldValue('city', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('Local Area') }}
                    </li>
                    <li>
                        <cic-select
                            id="site_area"
                            :enabled="editing"
                            :options="
                                areas[`_${item.countryId}`]
                                    ? areas[`_${item.countryId}`].map((x) => ({
                                          value: x.localAreaId,
                                          label: x.name,
                                      }))
                                    : []
                            "
                            :value="item.localAreaId > 0 ? item.localAreaId : null"
                            :placeholder="
                                $t(
                                    areas[`_${item.countryId}`] && areas[`_${item.countryId}`].length > 0
                                        ? 'Please select...'
                                        : 'None',
                                )
                            "
                            @input="(val) => updateFieldValue('localAreaId', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('County') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="site_county"
                            :enabled="editing"
                            :value="item.county"
                            :maxlength="64"
                            @input="(val) => updateFieldValue('county', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('Country') }}
                    </li>
                    <li>
                        <cic-select
                            id="site_country"
                            :enabled="editing"
                            :options="
                                countries
                                    .map((x) => ({ value: x.countryId, label: $t(x.name) }))
                                    .sort((a, b) => (a.label > b.label ? 1 : -1))
                            "
                            :value="item.countryId > 0 ? item.countryId : null"
                            @input="(val) => updateFieldValue('countryId', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('Postcode') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="site_postcode"
                            :enabled="editing"
                            :value="item.postcode"
                            :maxlength="16"
                            @input="(val) => updateFieldValue('postcode', val)"
                        />
                    </li>
                </template>
                <template v-if="$store.hasRight('EDIT_SITES')">
                    <li class="form-label">
                        {{ $t('Emergency Number Suffix') }}
                    </li>
                    <li>
                        <cic-select
                            id="site_suffix"
                            :enabled="editing"
                            :value="item.emergency.suffix"
                            :options="emergencySuffixes.map((suffix) => ({ value: suffix, label: suffix }))"
                            :maxlength="256"
                            allow-create
                            @input="(val) => updateEmergency('suffix', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('Emergency CLI') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="site_cli"
                            :enabled="editing"
                            :value="item.emergency.cli"
                            :maxlength="256"
                            @input="(val) => updateEmergency('cli', val)"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('Change Network CLI') }}
                    </li>
                    <li>
                        <cic-checkbox
                            id="site_change_cli"
                            :enabled="editing"
                            :value="item.emergency.changeCli"
                            @input="(val) => updateEmergency('changeCli', val)"
                        />
                    </li>
                </template>
            </ul>

            <template v-if="$store.hasRight('EDIT_SITES')">
                <h3>{{ $t('Site Parameters') }}</h3>
                <ul>
                    <li class="form-label">
                        {{ $t('UseSIPGateway') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-checkbox
                            id="site_sip_gateway"
                            :enabled="editing"
                            :value="item.parameters.UseSIPGateway === '1'"
                            @input="(val) => updateParameter('UseSIPGateway', val ? '1' : '0')"
                        />
                        <icon-button icon="help-icon" :title="$t('Use SIP gateway to route to RedCentrex')" />
                    </li>
                    <li class="form-label">
                        {{ $t('SIPTrunking_CallTimeout') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="site_sip_timeout"
                            :enabled="editing"
                            :value="item.parameters.SIPTrunking_CallTimeout"
                            @input="(val) => updateParameter('SIPTrunking_CallTimeout', val)"
                        />
                        <icon-button
                            icon="help-icon"
                            :title="$t('Configure call timeout for SIPTrunkingInbound.pll in NumberManagementOutbound')"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('SIPTrunking_Recording') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-checkbox
                            id="site_sip_record"
                            :enabled="editing"
                            :value="item.parameters.SIPTrunking_Recording === '1'"
                            @input="(val) => updateParameter('SIPTrunking_Recording', val ? '1' : '0')"
                        />
                        <icon-button
                            icon="help-icon"
                            :title="$t('Configure recording for SIPTrunkingInbound.pll in NumberManagementOutbound')"
                        />
                    </li>
                    <li class="form-label">
                        {{ $t('InternationalPrefix') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="site_intl_prefix"
                            :enabled="editing"
                            :value="item.parameters.InternationalPrefix"
                            @input="(val) => updateParameter('InternationalPrefix', val)"
                        />
                        <icon-button icon="help-icon" :title="$t('International prefix for the current location')" />
                    </li>
                    <template v-if="item.siteId === 0">
                        <li class="form-label">
                            {{ $t('TrustedOrganisation') }}
                        </li>
                        <li class="inline-form-item">
                            <cic-checkbox
                                id="site_trusted_org"
                                :enabled="editing"
                                :value="item.parameters.TrustedOrganisation === '1'"
                                @input="(val) => updateParameter('TrustedOrganisation', val ? '1' : '0')"
                            />
                        </li>
                    </template>
                </ul>
            </template>
        </div>
        <template
            v-if="
                $store.hasRight('ASSIGN_TO_SITES') &&
                (item.parameters.TrustedOrganisation === '1' || item.clis.length > 0)
            "
        >
            <h3 style="padding-left: 16px">{{ $t('Network CLI') }}</h3>
            <table id="network_cli_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Network CLI') }}</th>
                        <th>{{ $t('Default') }}</th>
                        <th>{{ $t('Reassign') }}</th>
                        <th>{{ $t('User Group') }}</th>
                        <th>{{ $t('Record CLI') }}</th>
                        <th v-if="item.parameters.TrustedOrganisation === '1'" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(cli, index) in item.clis">
                        <td>
                            <cic-textbox
                                :enabled="!originalClis.includes(cli.number)"
                                :value="cli.number"
                                @input="(val) => updateCli(index, 'number', val)"
                            />
                        </td>
                        <td>
                            <cic-checkbox
                                :value="cli.default"
                                :enabled="!cli.default"
                                @input="updateCli(index, 'default', true)"
                            />
                        </td>
                        <td>
                            <cic-select
                                v-if="originalClis.includes(cli.number)"
                                :options="
                                    sites.filter((x) => x.siteId > 0).map((x) => ({ value: x.siteId, label: x.name }))
                                "
                                :value="cli.siteId > 0 ? cli.siteId : null"
                                :placeholder="$t('Unassigned')"
                                clearable
                                @input="(val) => updateCli(index, 'siteId', val)"
                            />
                        </td>
                        <td>
                            <cic-select
                                :options="groups.map((x) => ({ value: x.userGroupId, label: x.name }))"
                                :value="cli.userGroupId > 0 ? cli.userGroupId : null"
                                :placeholder="$t('Unassigned')"
                                clearable
                                @input="(val) => updateCli(index, 'userGroupId', val)"
                            />
                        </td>
                        <td>
                            <cic-checkbox :value="cli.record" @input="(val) => updateCli(index, 'record', val)" />
                        </td>
                        <td v-if="item.parameters.TrustedOrganisation === '1'">
                            <icon-button icon="remove-icon" @click="removeCli(index)" />
                        </td>
                    </tr>
                    <tr v-if="item.parameters.TrustedOrganisation === '1'">
                        <td colspan="5"></td>
                        <td>
                            <icon-button icon="add-icon" @click="addCli" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>
    </cic-popup>
</template>

<script>
    import CicPopup from '@/components/CicPopup';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import IconButton from '@/components/controls/IconButton';

    export default {
        name: 'EditSite',
        components: {
            IconButton,
            CicCheckbox,
            CicPopup,
            CicSelect,
            CicTextbox,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            sites: {
                type: Array,
                required: true,
            },
            groups: {
                type: Array,
                required: true,
            },
            areas: {
                type: Object,
                required: true,
            },
            countries: {
                type: Array,
                required: true,
            },
            emergencySuffixes: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'input', 'updateFieldValue'],
        data() {
            return {
                editing: false,
                originalClis: [],
            };
        },
        created() {
            this.editing = !this.item.siteId || this.$store.hasRight('EDIT_SITES');
            this.originalClis = this.item.clis.map((x) => x.number);
        },
        methods: {
            updateEmergency(field, val) {
                const emergency = { ...this.item.emergency };
                emergency[field] = val;
                this.updateFieldValue('emergency', emergency);
            },
            updateFieldValue(field, val) {
                this.$emit('updateFieldValue', field, val);
            },
            updateParameter(field, val) {
                const parameters = { ...this.item.parameters };
                parameters[field] = val;
                this.updateFieldValue('parameters', parameters);
            },
            addCli() {
                const clis = [...this.item.clis];
                clis.push({ number: '', default: false, record: false, siteId: null, userGroupId: null });
                this.updateFieldValue('clis', clis);
            },
            removeCli(index) {
                const clis = [...this.item.clis];
                clis.splice(index, 1);
                this.updateFieldValue('clis', clis);
            },
            updateCli(index, field, val) {
                const clis = [...this.item.clis];
                if (field === 'default' && val && clis.some((x) => x.default)) {
                    clis[clis.findIndex((x) => x.default)].default = false;
                }

                clis[index][field] = val;

                this.updateFieldValue('clis', clis);
            },
        },
    };
</script>
